"use client";

import { IconCheckOutline18, IconChevronDownOutline18, IconCircleCheckOutline18, IconPaperPlane2Outline18, IconUserOutline18 } from "nucleo-ui-outline-18";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { TriggerArchetypeProps } from "./types";
import { usePollingListener } from "./usePollingListener";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_ACTIONS = ["approve", "reject", "view_details"];
const PAYLOAD_FIELDS = ["action_id", "value", "user", "channel", "message_ts", "response_url"] as const;
const POLL_INTERVAL_MS = 2_000;
const TIMEOUT_MS = 90_000;
type Phase = "compose" | "sending" | "waiting" | "captured";

/** Module-level cache for Slack user lists, keyed by connectorId. */
const slackUserCache = new Map<string, SlackUser[]>();

// ---------------------------------------------------------------------------
// Slack user type (from slack-list-users/test response)
// ---------------------------------------------------------------------------

interface SlackUser {
  id: string;
  name: string;
  real_name?: string;
  deleted: boolean;
  is_bot: boolean;
  profile?: {
    display_name?: string;
    real_name?: string;
    image_48?: string;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Title-case an action_id: "view_details" -> "View Details" */
function formatActionLabel(actionId: string): string {
  return actionId.replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

/** Display name for a Slack user, preferring display_name > real_name > name */
function getUserDisplayName(u: SlackUser): string {
  return u.profile?.display_name || u.real_name || u.name;
}

/** Classify an action_id into positive/negative/neutral based on keywords */
function classifyAction(actionId: string): "positive" | "negative" | "neutral" {
  const words = new Set(actionId.toLowerCase().split(/[_\-\s]+/));
  if (words.has("approve") || words.has("confirm") || words.has("yes") || words.has("accept")) return "positive";
  if (words.has("reject") || words.has("deny") || words.has("no") || words.has("cancel") || words.has("decline")) return "negative";
  return "neutral";
}

/** Button color based on action name keywords */
function getButtonStyle(actionId: string): string {
  const c = classifyAction(actionId);
  if (c === "positive") return "bg-emerald-600 hover:bg-emerald-700 text-white";
  if (c === "negative") return "bg-red-600 hover:bg-red-700 text-white";
  return "bg-ds-neutral-200 hover:bg-ds-neutral-300 text-zinc-800 dark:bg-ds-neutral-700 dark:hover:bg-zinc-600 dark:text-zinc-200";
}

/** Block Kit style for Slack buttons (primary = green, danger = red) */
function getSlackBlockStyle(actionId: string): "primary" | "danger" | undefined {
  const c = classifyAction(actionId);
  if (c === "positive") return "primary";
  if (c === "negative") return "danger";
  return undefined;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SlackInteraction archetype — sends a real Slack DM with Block Kit buttons
 * and captures the live interaction event.
 *
 * Flow: Compose -> Sending -> Waiting -> Captured
 *
 * Used for: slack-new-interaction-event
 */
export function SlackInteraction({
  triggerPayload,
  onRun,
  connectorId,
  onRegisterPrimaryAction
}: TriggerArchetypeProps) {
  // ---------------------------------------------------------------------------
  // Derived config
  // ---------------------------------------------------------------------------

  const actionIds = useMemo(() => {
    const configured = triggerPayload?.action_ids;
    if (Array.isArray(configured) && configured.length > 0) {
      return [...new Set(configured.map(String))];
    }
    return DEFAULT_ACTIONS;
  }, [triggerPayload]);

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [phase, setPhase] = useState<Phase>("compose");

  // Compose state
  const [users, setUsers] = useState<SlackUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<SlackUser | null>(null);

  // Waiting state
  const [sendError, setSendError] = useState<string | null>(null);

  // Captured state
  const [capturedPayload, setCapturedPayload] = useState<Record<string, string> | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  // User picker state
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const onRegisterPrimaryActionRef = useRef(onRegisterPrimaryAction);

  // Ref to hold the listener ID for polling (set after DM send)
  const listenerIdRef = useRef<string | null>(null);
  // Ref to pass detected payload from onPoll to onDetected without side effects in onPoll
  const detectedPayloadRef = useRef<Record<string, string> | null>(null);

  // ---------------------------------------------------------------------------
  // Polling via shared hook
  // ---------------------------------------------------------------------------

  const onPoll = useCallback(async (): Promise<boolean> => {
    const listenerId = listenerIdRef.current;
    if (!listenerId) return false;
    const pollRes = await authenticatedFetch<{
      code: number;
      data: Record<string, unknown>;
    }>(`${V2_ENDPOINT}/v1/test-listeners/${listenerId}`);
    if (!pollRes.ok) return false; // 404 = still waiting

    const pollJson = await pollRes.json();
    if (pollJson.data && Object.keys(pollJson.data).length > 0) {
      const payload: Record<string, string> = {};
      for (const key of PAYLOAD_FIELDS) {
        const val = pollJson.data[key];
        payload[key] = val != null ? String(val) : "";
      }
      detectedPayloadRef.current = payload;
      return true;
    }
    return false;
  }, []);
  const onDetected = useCallback(() => {
    if (detectedPayloadRef.current) {
      setCapturedPayload(detectedPayloadRef.current);
      setOverrides({});
      detectedPayloadRef.current = null;
    }
    setPhase("captured");
  }, []);
  const {
    secondsRemaining,
    error: pollingError,
    stopListening,
    startListening: hookStart
  } = usePollingListener({
    pollIntervalMs: POLL_INTERVAL_MS,
    timeoutMs: TIMEOUT_MS,
    timeoutMessage: "Timed out waiting for interaction. The DM was sent — try clicking a button in Slack and sending again.",
    onPoll,
    onDetected
  });

  // Close user dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------------------------------------------------------------------
  // Fetch Slack users on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!connectorId) return;

    // Use cached result if available (avoids re-fetching on every modal open)
    const cached = slackUserCache.get(connectorId);
    if (cached) {
      setUsers(cached);
      setUsersLoading(false);
      return;
    }
    let cancelled = false;
    setUsersLoading(true);
    setUsersError(null);
    (async () => {
      try {
        const response = await authenticatedFetch<{
          data: {
            result: {
              success: boolean;
              data: {
                ret: {
                  ok: boolean;
                  members: SlackUser[];
                };
              };
            };
          };
        }>(`${V2_ENDPOINT}/v1/actions/slack-list-users/test`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            inputs: {
              limit: 200
            },
            connector: {
              id: connectorId,
              type: "slack"
            }
          })
        });
        if (cancelled) return;
        if (!response.ok) {
          setUsersError("Failed to fetch Slack users");
          return;
        }
        const json = await response.json();
        const members = (json.data?.result?.data?.ret?.members ?? []) as SlackUser[];
        const humans = members.filter(u => !u.is_bot && !u.deleted && u.id !== "USLACKBOT");
        setUsers(humans);
        slackUserCache.set(connectorId, humans);
      } catch {
        if (!cancelled) setUsersError("Failed to fetch Slack users");
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [connectorId]);

  // ---------------------------------------------------------------------------
  // Filtered user list
  // ---------------------------------------------------------------------------

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;
    const q = searchQuery.toLowerCase();
    return users.filter(u => u.name.toLowerCase().includes(q) || (u.real_name?.toLowerCase().includes(q) ?? false) || (u.profile?.display_name?.toLowerCase().includes(q) ?? false));
  }, [users, searchQuery]);

  // ---------------------------------------------------------------------------
  // Send DM flow
  // ---------------------------------------------------------------------------

  const handleSendDM = useCallback(async () => {
    if (!selectedUser || !connectorId) return;
    setPhase("sending");
    setSendError(null);
    try {
      // 1. Build Block Kit blocks
      const blocks = [{
        type: "section",
        text: {
          type: "mrkdwn",
          text: "Click a button to test your workflow"
        }
      }, {
        type: "actions",
        elements: actionIds.map(id => {
          const style = getSlackBlockStyle(id);
          return {
            type: "button",
            text: {
              type: "plain_text",
              text: formatActionLabel(id)
            },
            action_id: id,
            value: id,
            ...(style ? {
              style
            } : {})
          };
        })
      }];

      // 2. Send DM first — we need the message_ts to scope the listener
      const sendRes = await authenticatedFetch<{
        data: {
          result: {
            data: {
              ret: {
                ts?: string;
                channel?: string;
              };
            };
          };
        };
      }>(`${V2_ENDPOINT}/v1/actions/slack-send-message-to-user-or-group/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inputs: {
            users: selectedUser.id,
            text: "Test: Click a button to test your workflow",
            blocks
          },
          connector: {
            id: connectorId,
            type: "slack"
          }
        })
      });
      if (!sendRes.ok) {
        throw new Error("Failed to send Slack DM");
      }

      // Extract message_ts from the send response to scope the listener
      const sendJson = await sendRes.json();
      const messageTs = sendJson.data?.result?.data?.ret?.ts;

      // 3. Create test listener scoped to this exact message
      const filters: Record<string, string> = {
        user: selectedUser.id
      };
      if (messageTs) filters.message_ts = messageTs;
      const listenerRes = await authenticatedFetch<{
        code: number;
        data: {
          id: string;
        };
      }>(`${V2_ENDPOINT}/v1/test-listeners`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          triggerType: "slack-new-interaction-event",
          filters
        })
      });
      if (!listenerRes.ok) {
        throw new Error("Failed to create test listener");
      }
      const listenerJson = await listenerRes.json();
      const newListenerId = listenerJson.data?.id;
      if (!newListenerId) throw new Error("No listener ID returned");

      // 4. Start polling via shared hook
      listenerIdRef.current = newListenerId;
      setPhase("waiting");
      hookStart();
    } catch (err) {
      setPhase("compose");
      setSendError(err instanceof Error ? err.message : "Something went wrong");
    }
  }, [selectedUser, connectorId, actionIds, hookStart]);
  const openUserPicker = useCallback(() => {
    setUserDropdownOpen(true);
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  }, []);
  useEffect(() => {
    onRegisterPrimaryActionRef.current = onRegisterPrimaryAction;
  }, [onRegisterPrimaryAction]);

  // ---------------------------------------------------------------------------
  // Cancel waiting
  // ---------------------------------------------------------------------------

  // When the polling hook times out, transition back to compose and show error
  useEffect(() => {
    if (pollingError) {
      listenerIdRef.current = null;
      setPhase("compose");
      setSendError(pollingError);
    }
  }, [pollingError]);
  const handleCancel = useCallback(() => {
    stopListening();
    listenerIdRef.current = null;
    setPhase("compose");
  }, [stopListening]);

  // ---------------------------------------------------------------------------
  // Override handling (captured phase)
  // ---------------------------------------------------------------------------

  const handleOverrideChange = useCallback((key: string, value: string) => {
    setOverrides(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Notify parent whenever overrides change on a captured payload.
  useEffect(() => {
    if (capturedPayload) {
      onRun({
        ...capturedPayload,
        ...overrides,
        triggeredAt: new Date().toISOString()
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onRun is a stable state setter from the modal shell
  }, [capturedPayload, overrides]);
  const currentPayload = useMemo(() => capturedPayload ? {
    ...capturedPayload,
    ...overrides
  } : null, [capturedPayload, overrides]);

  // Register a single primary footer CTA for this archetype.
  // Before capture, the footer drives member selection / DM sending.
  // After capture, we release control so the shell's "Run Workflow" button returns.
  useEffect(() => {
    const registerPrimaryAction = onRegisterPrimaryActionRef.current;
    if (!registerPrimaryAction) return;
    if (phase === "captured") {
      registerPrimaryAction(null);
      return;
    }
    if (phase === "sending") {
      registerPrimaryAction({
        label: "Sending DM...",
        onClick: () => {},
        disabled: true,
        leadingIcon: <PixelLoader size="xs" />
      });
      return;
    }
    if (phase === "waiting") {
      registerPrimaryAction({
        label: "Waiting for interaction...",
        onClick: () => {},
        disabled: true,
        leadingIcon: <PixelLoader size="xs" />
      });
      return;
    }
    registerPrimaryAction(selectedUser ? {
      label: `Send DM to ${getUserDisplayName(selectedUser)}`,
      onClick: handleSendDM,
      leadingIcon: <IconPaperPlane2Outline18 className="h-3.5 w-3.5" />
    } : {
      label: usersLoading ? "Loading Slack members..." : "Select Slack Member",
      onClick: openUserPicker,
      disabled: usersLoading,
      leadingIcon: usersLoading ? <PixelLoader size="xs" /> : <IconUserOutline18 className="h-3.5 w-3.5" />
    });
  }, [handleSendDM, openUserPicker, phase, selectedUser, usersLoading]);
  useEffect(() => {
    return () => onRegisterPrimaryActionRef.current?.(null);
  }, []);

  // ---------------------------------------------------------------------------
  // No connector
  // ---------------------------------------------------------------------------

  if (!connectorId) {
    return <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-800/50 dark:bg-amber-900/20">
        <p className="text-sm text-amber-700 dark:text-amber-300">
          No Slack connector found. Connect a Slack workspace on the trigger node to send a test DM.
        </p>
      </div>;
  }

  // ---------------------------------------------------------------------------
  // Render: Compose
  // ---------------------------------------------------------------------------

  if (phase === "compose") {
    return <div className="space-y-3">
        {/* Error from previous attempt */}
        {sendError && <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2.5 dark:border-amber-800/50 dark:bg-amber-900/20">
            <p className="label-xs text-amber-700 dark:text-amber-300">{sendError}</p>
          </div>}
        {/* User picker — dropdown style matching SlackChannelSelector */}
        <div className="space-y-2" ref={dropdownRef}>
          <label className="field-label">Send test DM to</label>

          <div className="relative">
            {userDropdownOpen ? <input ref={searchInputRef} type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search members..." autoFocus className="text-ds-text-primary placeholder:text-ds-text-disabled bg-ds-surface-card w-full rounded-lg border border-blue-500 px-3 py-2 text-sm ring-2 ring-blue-500/20 dark:border-blue-500" /> : <button type="button" onClick={() => setUserDropdownOpen(true)} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600">
                {selectedUser ? <span className="flex items-center gap-2">
                    {selectedUser.profile?.image_48 ? <img src={selectedUser.profile.image_48} alt="" className="h-5 w-5 rounded-full object-cover" /> : <span className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-5 w-5 items-center justify-center rounded-full">
                        <IconUserOutline18 className="text-ds-text-disabled h-3.5 w-3.5" />
                      </span>}
                    <span>{getUserDisplayName(selectedUser)}</span>
                  </span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a member...</span>}
                <IconChevronDownOutline18 className="text-ds-text-disabled h-4 w-4" />
              </button>}

            {/* Dropdown list */}
            {userDropdownOpen && <div className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border">
                {usersLoading && <div className="text-ds-text-disabled flex items-center gap-2 px-3 py-2 text-sm">
                    <PixelLoader size="sm" />
                    Loading members...
                  </div>}

                {usersError && <div className="px-3 py-2 text-sm text-red-500">{usersError}</div>}

                {!usersLoading && !usersError && filteredUsers.length === 0 && <div className="text-ds-text-disabled px-3 py-2 text-sm">
                    {searchQuery ? "No members match your search" : "No members found"}
                  </div>}

                {!usersLoading && filteredUsers.map(u => {
              const isSelected = selectedUser?.id === u.id;
              return <button key={u.id} type="button" onClick={() => {
                setSelectedUser(u);
                setUserDropdownOpen(false);
                setSearchQuery("");
              }} className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${isSelected ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" : "text-ds-text-primary"}`}>
                        {u.profile?.image_48 ? <img src={u.profile.image_48} alt="" className="h-6 w-6 flex-shrink-0 rounded-full object-cover" /> : <div className="bg-ds-neutral-200 dark:bg-ds-neutral-700 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full">
                            <IconUserOutline18 className="text-ds-text-muted h-3 w-3" />
                          </div>}
                        <span className="min-w-0 flex-1 truncate">{getUserDisplayName(u)}</span>
                        {isSelected && <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-blue-500" />}
                      </button>;
            })}
              </div>}
          </div>
        </div>
        {/* Message preview */}
        <div className="space-y-1.5 pb-3">
          <label className="field-label">Message preview</label>
          <div className="border-ds-border-default bg-ds-surface-card rounded-xl border px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-600">
                <span className="text-sm font-bold text-white">B3</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-body text-ds-text-primary font-bold">B3OS</span>
                  <Badge variant="outline" className="py-0">
                    APP
                  </Badge>
                </div>
                <p className="label-xs mt-1 leading-relaxed">Click a button to test your workflow</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {actionIds.map(actionId => <div key={actionId} className={`rounded px-3 py-1.5 text-xs font-bold ${getButtonStyle(actionId)}`}>
                      {formatActionLabel(actionId)}
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {!onRegisterPrimaryAction && <Button onClick={selectedUser ? handleSendDM : openUserPicker} disabled={usersLoading} className="w-full">
            {usersLoading ? <PixelLoader size="sm" /> : selectedUser ? <IconPaperPlane2Outline18 className="h-4 w-4" /> : <IconUserOutline18 className="h-4 w-4" />}
            {usersLoading ? "Loading Slack members..." : selectedUser ? `Send DM to ${getUserDisplayName(selectedUser)}` : "Select Slack Member"}
          </Button>}
      </div>;
  }

  // ---------------------------------------------------------------------------
  // Render: Sending
  // ---------------------------------------------------------------------------

  if (phase === "sending") {
    return <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <PixelLoader size="md" color="blue" />
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Sending DM...</p>
            <p className="label-xs text-blue-500 dark:text-blue-500">Creating listener and sending Block Kit message</p>
          </div>
        </div>
      </div>;
  }

  // ---------------------------------------------------------------------------
  // Render: Waiting
  // ---------------------------------------------------------------------------

  if (phase === "waiting") {
    return <div className="space-y-3">
        <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-800 dark:bg-blue-900/20">
          <PixelLoader size="md" color="blue" />
          <div>
            <p className="text-sm font-medium text-blue-700 dark:text-blue-400">Waiting for interaction...</p>
            <p className="label-xs text-blue-500 dark:text-blue-500">
              {secondsRemaining}s remaining. Click a button in the Slack DM.
            </p>
          </div>
        </div>
        {selectedUser && <div className="text-ds-text-muted flex items-center gap-2 px-1">
            <IconPaperPlane2Outline18 className="text-ds-text-disabled h-3.5 w-3.5" />
            <span className="label-xs">
              DM sent to <span className="text-ds-text-primary font-semibold">{getUserDisplayName(selectedUser)}</span>
            </span>
          </div>}
        <Button variant="white" onClick={handleCancel} className="w-full">
          Cancel
        </Button>
      </div>;
  }

  // ---------------------------------------------------------------------------
  // Render: Captured
  // ---------------------------------------------------------------------------

  return <div className="space-y-3">
      {/* Success indicator */}
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2.5 dark:border-emerald-800/50 dark:bg-emerald-900/20">
        <IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Interaction captured!</span>
      </div>
      {/* Payload fields */}
      {currentPayload && <div className="border-ds-border-default space-y-2 rounded-lg border px-3 py-3">
          {PAYLOAD_FIELDS.map(key => <div key={key}>
              <label htmlFor={`slack-field-${key}`} className="field-label mb-1 block">
                {key}
              </label>
              <input id={`slack-field-${key}`} type="text" value={currentPayload[key] ?? ""} onChange={e => handleOverrideChange(key, e.target.value)} className="border-ds-border-default text-ds-text-primary bg-ds-surface-card w-full rounded-md border px-2.5 py-1.5 font-mono text-xs" />
            </div>)}
        </div>}
    </div>;
}